import classNames from 'classnames';
import React, { forwardRef } from 'react';
import type { DistributedOmit } from 'type-fest';
import addTestId from '../utils/addTestId';
import type { LinkProps } from '../utils/router/Link';
import Link from '../utils/router/Link';
import * as styles from './Button.module.scss';
import LoadingIndicator from './LoadingIndicator';
import type { SVGIconSize } from './SVGIcon';
import SVGIcon from './SVGIcon';

type ButtonSize = 'large' | 'micro' | 'small' | 'regular';

type BasicButtonProps = {
  active?: boolean;
  block?: boolean;
  danger?: boolean;
  disabled?: boolean;
  size?: ButtonSize;
  variant?: 'link' | 'primary' | 'secondary' | 'muted';
  icon?: React.ReactNode;
  testId?: string;
  iconSize?: SVGIconSize;
};

type HtmlButtonProps = React.ComponentPropsWithoutRef<'button'>;

type LinkSpecificProps = Exclude<
  keyof LinkProps,
  keyof HtmlButtonProps | keyof BasicButtonProps
>;

type HtmlButtonSpecificProps = Exclude<
  keyof HtmlButtonProps,
  keyof LinkProps | keyof BasicButtonProps
>;

export type ButtonProps = BasicButtonProps &
  (
    | ({ to: LinkProps['to'] } & LinkProps & {
          [Property in HtmlButtonSpecificProps]?: never;
        })
    | ({ to?: never } & HtmlButtonProps & {
          [Property in LinkSpecificProps]?: never;
        })
  );

const Button = forwardRef<HTMLElement, ButtonProps>(
  (
    {
      active,
      block,
      danger,
      disabled,
      size = 'regular',
      variant,
      icon,
      testId,
      className,
      children,
      iconSize: iconSizeProp,
      ...props
    },
    ref,
  ) => {
    const classes = classNames(styles.basic, className, {
      [styles.active]: active,
      [styles.block]: block,
      [styles.danger]: danger,
      [styles.disabled]: disabled,
      [styles.large]: size === 'large',
      [styles.link]: variant === 'link',
      [styles.micro]: size === 'micro',
      [styles.muted]: variant === 'muted',
      [styles.primary]: variant === 'primary',
      [styles.regularSize]: size === 'regular',
      [styles.secondary]: variant === 'secondary',
      [styles.small]: size === 'small',
    });

    const iconSize = iconSizeProp ?? ButtonSizeToIconSize[size];

    const content = (
      <>
        {!!icon && (
          <div
            className={classNames(styles.iconContainer, {
              [styles.iconGap]: !!children,
            })}
            aria-hidden
          >
            {typeof icon === 'string' ? (
              <SVGIcon size={iconSize}>{icon}</SVGIcon>
            ) : (
              icon
            )}
          </div>
        )}
        {children}
      </>
    );

    if (typeof props.to !== 'undefined') {
      return (
        <Link
          className={classes}
          {...props}
          {...addTestId(testId)}
          ref={ref as React.ForwardedRef<HTMLAnchorElement>}
        >
          {content}
        </Link>
      );
    }

    return (
      <button
        className={classes}
        {...props}
        disabled={disabled}
        // eslint-disable-next-line react/button-has-type
        type={props.type || 'button'}
        {...addTestId(testId)}
        ref={ref as React.ForwardedRef<HTMLButtonElement>}
      >
        {content}
      </button>
    );
  },
);

const ButtonSizeToIconSize: Record<ButtonSize, SVGIconSize> = {
  large: 'large',
  micro: 'small',
  regular: 'regular',
  small: 'small',
};

export type ButtonWithLoadingStateProps = DistributedOmit<
  ButtonProps,
  'variant'
> & {
  loading: boolean;
  variant?: Exclude<ButtonProps['variant'], 'link'>;
};

export const ButtonWithLoadingState = ({
  children,
  loading,
  disabled,
  ...props
}: ButtonWithLoadingStateProps) => {
  return (
    <Button disabled={disabled || loading} {...props}>
      <span
        className={classNames(styles.loadingStateContainer, {
          [styles.isLoading]: loading,
        })}
      >
        {loading && (
          <span className={styles.loadingIndicatorContainer}>
            <LoadingIndicator delay={0} adaptColor />
          </span>
        )}
        <span className={styles.inner}>{children}</span>
      </span>
    </Button>
  );
};

type IconButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const IconButtonSizeToIconSize: Record<IconButtonSize, SVGIconSize> = {
  xs: 14,
  sm: 'regular',
  md: 'regular',
  lg: 'regular',
  xl: 'large',
};

export type IconButtonProps = DistributedOmit<
  React.ComponentPropsWithoutRef<typeof Button>,
  'iconSize' | 'size' | 'variant'
> & {
  applyNegativeMargin?: boolean;
  children: string | React.ReactNode;
  'aria-label': string;
  variant?: 'muted';
  size?: IconButtonSize;
};

export const IconButton = React.forwardRef<HTMLElement, IconButtonProps>(
  (
    {
      active,
      applyNegativeMargin = false,
      children,
      className,
      size = 'lg',
      ...props
    }: IconButtonProps,
    ref,
  ) => (
    <Button
      {...props}
      ref={ref}
      active={active}
      className={classNames(styles.iconButton, [styles[size]], className, {
        [styles.active]: active,
        [styles.negativeMargin]: applyNegativeMargin,
      })}
      icon={children}
      iconSize={IconButtonSizeToIconSize[size]}
    />
  ),
);

export default Button;
